import { FaBrain, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { motion, useMotionValue, useTransform } from "framer-motion";

const T = {
  bgCard: "#0a1628",
  border: "#1a2d4a",
  accent: "#eab308",
  accentDim: "rgba(234,179,8,0.1)",
  accentGlow: "rgba(234,179,8,0.28)",
  purple: "#8b5cf6",
  purpleDim: "rgba(139,92,246,0.15)",
  purpleGlow: "rgba(139,92,246,0.3)",
  textPrimary: "#f0f6ff",
  textSecondary: "#94a3b8",
  textMuted: "#4b6280",
  success: "#22c55e",
};

export function TiltCustomCard({ customAmount, setCustomAmount, navigate }) {
  const { isLoggedIn } = useAuth();

  /* 3D tilt – same pattern as TiltCard */
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-100, 100], [10, -10]);
  const rotateY = useTransform(x, [-100, 100], [-10, 10]);

  function handleMouse(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  }

  const goToInvestment = () => {
    const amount = Number(customAmount);
    if (!amount || amount < 100) {
      alert("Minimum investment is $100");
      return;
    }
    const payload = { amount: String(amount), name: "custom_plan" };
    if (!isLoggedIn) {
      navigate("/login", { state: { from: "/investment", planPayload: payload } });
      return;
    }
    navigate("/investment", { state: payload });
  };

  return (
    /* entrance: from bottom-right diagonal — delay 0.55s so it lands last */
    <motion.div
      initial={{ opacity: 0, x: 50, y: 50, scale: 0.92 }}
      whileInView={{ opacity: 1, x: 0, y: 0, scale: 1 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.55 }}
      style={{ perspective: 1200 }}
      onMouseMove={handleMouse}
      onMouseLeave={() => { x.set(0); y.set(0); }}
    >
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        whileHover={{ scale: 1.02 }}
        transition={{ type: "spring", stiffness: 280, damping: 26 }}
      >
        <div style={{
          background: T.bgCard,
          border: `1px solid rgba(139,92,246,0.4)`,
          borderRadius: 22,
          padding: "24px 22px",
          position: "relative",
          overflow: "hidden",
          boxShadow: `0 0 0 1px rgba(139,92,246,0.12), 0 20px 56px rgba(0,0,0,0.45), 0 0 40px ${T.purpleGlow}`,
        }}>

          {/* top accent line – purple→gold */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 3,
            background: "linear-gradient(90deg,#7c3aed,#eab308)",
          }} />

          {/* corner glow */}
          <div style={{
            position: "absolute", top: -24, right: -24,
            width: 100, height: 100, borderRadius: "50%",
            background: `radial-gradient(ellipse,${T.purpleGlow} 0%,transparent 70%)`,
            pointerEvents: "none",
          }} />

          {/* shimmer on mount */}
          <motion.div
            initial={{ x: "-120%" }}
            whileInView={{ x: "120%" }}
            viewport={{ once: true }}
            transition={{ delay: 0.8, duration: 0.85, ease: "easeInOut" }}
            style={{
              position: "absolute", inset: 0,
              background: "linear-gradient(105deg,transparent 30%,rgba(255,255,255,0.04) 50%,transparent 70%)",
              pointerEvents: "none", zIndex: 1,
            }}
          />

          {/* ── Icon ── */}
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7, type: "spring", stiffness: 260, damping: 18 }}
            style={{
              width: 60, height: 60, borderRadius: 18,
              background: "linear-gradient(135deg,#5b21b6,#7c3aed)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: `0 8px 28px ${T.purpleGlow}`,
              position: "relative", zIndex: 2,
            }}
          >
            <FaBrain size={24} color="#fff" />
          </motion.div>

          {/* title */}
          <div style={{ textAlign: "center", marginBottom: 20, position: "relative", zIndex: 2 }}>
            <h3 style={{
              fontSize: "1.1rem", fontWeight: 900,
              color: T.textPrimary, letterSpacing: "-0.02em",
              textTransform: "uppercase", margin: "0 0 4px",
            }}>
              Custom Plan
            </h3>
            <span style={{
              fontSize: 9, fontWeight: 900, color: T.purple,
              letterSpacing: "0.25em", textTransform: "uppercase",
            }}>
              Flexible Investment
            </span>
          </div>

          {/* amount input box */}
          <div style={{
            background: "rgba(0,0,0,0.3)",
            border: `1px solid rgba(255,255,255,0.06)`,
            borderRadius: 16, padding: "16px 14px",
            marginBottom: 18, position: "relative", zIndex: 2,
          }}>
            <label style={{
              display: "block", fontSize: 10, fontWeight: 800,
              color: T.textMuted, textTransform: "uppercase",
              letterSpacing: "0.15em", marginBottom: 10,
            }}>
              Enter Investment Amount
            </label>
            <input
              type="number"
              min="100"
              value={customAmount}
              onChange={(e) => setCustomAmount(e.target.value)}
              placeholder="Minimum $100"
              style={{
                width: "100%",
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.border}`,
                borderRadius: 12,
                padding: "11px 14px",
                color: T.textPrimary,
                fontSize: 16, fontWeight: 900,
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
              onFocus={e => e.currentTarget.style.borderColor = "#8b5cf6"}
              onBlur={e => e.currentTarget.style.borderColor = T.border}
            />
          </div>

          {/* ROI info rows */}
          <div style={{ marginBottom: 20, position: "relative", zIndex: 2 }}>
            {[
              { label: "Dynamic ROI", value: "2% – 10%", valColor: T.success },
              { label: "Minimum Amount", value: "$100", valColor: T.textPrimary },
            ].map((row, i) => (
              <div key={i}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "9px 2px",
                }}>
                  <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                    {row.label}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 900, color: row.valColor }}>
                    {row.value}
                  </span>
                </div>
                {i === 0 && (
                  <div style={{ height: 1, background: "linear-gradient(90deg,transparent,rgba(139,92,246,0.25),transparent)" }} />
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <motion.button
            whileHover={{ scale: 1.03, boxShadow: `0 12px 36px ${T.purpleGlow}` }}
            whileTap={{ scale: 0.97 }}
            onClick={goToInvestment}
            style={{
              width: "100%", padding: "14px 0",
              background: "linear-gradient(135deg,#5b21b6,#7c3aed)",
              color: "#fff",
              fontWeight: 900, fontSize: 11,
              letterSpacing: "0.18em", textTransform: "uppercase",
              border: "none", borderRadius: 14, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              boxShadow: `0 6px 24px ${T.purpleGlow}`,
              transition: "box-shadow 0.25s",
              position: "relative", zIndex: 2,
            }}
          >
            Create Custom Plan <FaArrowRight size={11} />
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}